// The Mayse prompt library — the questions a session walks through.
//
// warmth is an emotional-effort tag, used to shape the arc of a session:
//   W1 — gentle, concrete warm-ups (easy to answer, low vulnerability)
//   W2 — reflective, story-shaped (a memory with some feeling behind it)
//   W3 — tender / legacy (deeper, more personal — earned later in a session)
//
// starter is a first-person sentence opener we show the storyteller so their
// answer stands on its own (e.g. transcript reads "A toy I loved was…" rather
// than "A yo-yo"). It's a suggestion, not a script.
//
// The array order IS the default session order: it opens warm (W1) and eases
// toward the tender questions (W3), so a session builds trust before it asks
// for much.
//
// TODO(HEL-53): this is a representative ~20-question seed spanning topics and
// warmth levels. Import the full ~75-prompt library from the Linear doc
// "Seed Prompt Library v1" when Linear access is available.

export type Warmth = "W1" | "W2" | "W3";

export interface Question {
  id: string;
  text: string;
  starter: string;
  topic: string;
  warmth: Warmth;
}

export const QUESTIONS: Question[] = [
  // ---- W1 · gentle warm-ups ----
  {
    id: "childhood-toy",
    text: "What was a toy or game you loved as a child?",
    starter: "My favorite toy (or game) as a child was…",
    topic: "childhood",
    warmth: "W1",
  },
  {
    id: "childhood-home",
    text: "What did the home you grew up in look like?",
    starter: "The home I grew up in…",
    topic: "home",
    warmth: "W1",
  },
  {
    id: "comfort-food",
    text: "Is there a meal or dish that tastes like home to you?",
    starter: "The dish that tastes like home to me is…",
    topic: "food",
    warmth: "W1",
  },
  {
    id: "ordinary-weekend",
    text: "What did an ordinary weekend look like when you were young?",
    starter: "An ordinary weekend for us looked like…",
    topic: "everyday",
    warmth: "W1",
  },
  {
    id: "neighborhood",
    text: "Tell me about the street or neighborhood you grew up on.",
    starter: "The neighborhood I grew up on…",
    topic: "place",
    warmth: "W1",
  },
  {
    id: "song-back-in-time",
    text: "Is there a song that takes you straight back in time?",
    starter: "A song that takes me right back is…",
    topic: "everyday",
    warmth: "W1",
  },

  // ---- W2 · reflective, story-shaped ----
  {
    id: "first-days-school",
    text: "What do you remember about your first days of school?",
    starter: "In my first days of school, I remember…",
    topic: "childhood",
    warmth: "W2",
  },
  {
    id: "first-job",
    text: "What was your first job, and what did you take away from it?",
    starter: "My first job was…",
    topic: "work",
    warmth: "W2",
  },
  {
    id: "childhood-friend",
    text: "Tell me about a friend who mattered to you growing up.",
    starter: "A friend who mattered to me growing up was…",
    topic: "friendship",
    warmth: "W2",
  },
  {
    id: "meeting-partner",
    text: "How did you meet your partner, or a great love of your life?",
    starter: "The way we met was…",
    topic: "love",
    warmth: "W2",
  },
  {
    id: "family-tradition",
    text: "Was there a family tradition you always looked forward to?",
    starter: "A family tradition I looked forward to was…",
    topic: "family",
    warmth: "W2",
  },
  {
    id: "grandparents",
    text: "What do you remember about your grandparents?",
    starter: "What I remember about my grandparents is…",
    topic: "family",
    warmth: "W2",
  },
  {
    id: "place-that-shaped-you",
    text: "Tell me about a place you lived that shaped who you became.",
    starter: "A place that shaped who I became was…",
    topic: "place",
    warmth: "W2",
  },
  {
    id: "proud-work",
    text: "What's a piece of work or a project you were proud of?",
    starter: "A project I was proud of was…",
    topic: "work",
    warmth: "W2",
  },

  // ---- W3 · tender / legacy ----
  {
    id: "hard-season",
    text: "Was there a hard season in your life that changed you?",
    starter: "A hard season that changed me was…",
    topic: "milestones",
    warmth: "W3",
  },
  {
    id: "lesson-learned",
    text: "What's something life taught you that you wish you'd known sooner?",
    starter: "Something life taught me that I wish I'd known sooner is…",
    topic: "values",
    warmth: "W3",
  },
  {
    id: "your-parents",
    text: "What would you want your children to know about your own parents?",
    starter: "What I'd want my children to know about my parents is…",
    topic: "family",
    warmth: "W3",
  },
  {
    id: "becoming-parent",
    text: "What do you remember about becoming a parent?",
    starter: "When I became a parent…",
    topic: "milestones",
    warmth: "W3",
  },
  {
    id: "most-proud",
    text: "When you look back, what are you most proud of?",
    starter: "Looking back, I'm most proud of…",
    topic: "values",
    warmth: "W3",
  },
  {
    id: "one-piece-advice",
    text: "If you could pass on one piece of advice, what would it be?",
    starter: "The one piece of advice I'd pass on is…",
    topic: "legacy",
    warmth: "W3",
  },
  {
    id: "how-remembered",
    text: "How would you like to be remembered?",
    starter: "I'd like to be remembered as…",
    topic: "legacy",
    warmth: "W3",
  },
  {
    id: "unsaid",
    text: "Is there something you'd want to say to someone that you never got to say?",
    starter: "Something I never got to say is…",
    topic: "love",
    warmth: "W3",
  },
];
